import React from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div data-component-id="src/components/ui/Input.tsx:28:6" data-component-path="src/components/ui/Input.tsx" data-component-line="28" data-component-end-line="71" data-component-file="Input.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%22%7D" className="w-full">
        {label && (
          <label data-component-id="src/components/ui/Input.tsx:30:10" data-component-path="src/components/ui/Input.tsx" data-component-line="30" data-component-end-line="35" data-component-file="Input.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-foreground%20mb-1.5%22%7D"
            htmlFor={inputId}
            className="block text-sm font-medium text-foreground mb-1.5"
          >
            {label}
          </label>
        )}
        <div data-component-id="src/components/ui/Input.tsx:37:8" data-component-path="src/components/ui/Input.tsx" data-component-line="37" data-component-end-line="64" data-component-file="Input.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
          {leftIcon && (
            <div data-component-id="src/components/ui/Input.tsx:39:12" data-component-path="src/components/ui/Input.tsx" data-component-line="39" data-component-end-line="41" data-component-file="Input.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-y-0%20left-0%20pl-3%20flex%20items-center%20pointer-events-none%20text-muted-foreground%22%7D" className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
              {leftIcon}
            </div>
          )}
          <input data-component-id="src/components/ui/Input.tsx:43:10" data-component-path="src/components/ui/Input.tsx" data-component-line="43" data-component-end-line="58" data-component-file="Input.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22id%22%3A%22%5Bvar%3AinputId%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
            ref={ref}
            id={inputId}
            className={`
              w-full px-4 py-2.5 rounded-lg border bg-card text-foreground
              placeholder:text-muted-foreground
              focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all duration-200
              ${leftIcon ? 'pl-10' : ''}
              ${rightIcon ? 'pr-10' : ''}
              ${error ? 'border-destructive focus:ring-destructive' : 'border-input'}
              ${className}
            `}
            {...props}
          />
          {rightIcon && (
            <div data-component-id="src/components/ui/Input.tsx:60:12" data-component-path="src/components/ui/Input.tsx" data-component-line="60" data-component-end-line="62" data-component-file="Input.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-y-0%20right-0%20pr-3%20flex%20items-center%20text-muted-foreground%22%7D" className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p data-component-id="src/components/ui/Input.tsx:66:10" data-component-path="src/components/ui/Input.tsx" data-component-line="66" data-component-end-line="66" data-component-file="Input.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1.5%20text-sm%20text-destructive%22%7D" className="mt-1.5 text-sm text-destructive">{error}</p>
        )}
        {hint && !error && (
          <p data-component-id="src/components/ui/Input.tsx:69:10" data-component-path="src/components/ui/Input.tsx" data-component-line="69" data-component-end-line="69" data-component-file="Input.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1.5%20text-sm%20text-muted-foreground%22%7D" className="mt-1.5 text-sm text-muted-foreground">{hint}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, className = '', id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div data-component-id="src/components/ui/Input.tsx:89:6" data-component-path="src/components/ui/Input.tsx" data-component-line="89" data-component-end-line="116" data-component-file="Input.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%22%7D" className="w-full">
        {label && (
          <label data-component-id="src/components/ui/Input.tsx:91:10" data-component-path="src/components/ui/Input.tsx" data-component-line="91" data-component-end-line="96" data-component-file="Input.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-foreground%20mb-1.5%22%7D"
            htmlFor={inputId}
            className="block text-sm font-medium text-foreground mb-1.5"
          >
            {label}
          </label>
        )}
        <textarea data-component-id="src/components/ui/Input.tsx:98:8" data-component-path="src/components/ui/Input.tsx" data-component-line="98" data-component-end-line="111" data-component-file="Input.tsx" data-component-name="textarea" data-component-content="%7B%22elementName%22%3A%22textarea%22%2C%22id%22%3A%22%5Bvar%3AinputId%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
          ref={ref}
          id={inputId}
          className={`
            w-full px-4 py-2.5 rounded-lg border bg-card text-foreground
            placeholder:text-muted-foreground
            focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-all duration-200 resize-y min-h-[100px]
            ${error ? 'border-destructive focus:ring-destructive' : 'border-input'}
            ${className}
          `}
          {...props}
        />
        {error && <p data-component-id="src/components/ui/Input.tsx:112:18" data-component-path="src/components/ui/Input.tsx" data-component-line="112" data-component-end-line="112" data-component-file="Input.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1.5%20text-sm%20text-destructive%22%7D" className="mt-1.5 text-sm text-destructive">{error}</p>}
        {hint && !error && (
          <p data-component-id="src/components/ui/Input.tsx:114:10" data-component-path="src/components/ui/Input.tsx" data-component-line="114" data-component-end-line="114" data-component-file="Input.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1.5%20text-sm%20text-muted-foreground%22%7D" className="mt-1.5 text-sm text-muted-foreground">{hint}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  options: { value: string; label: string }[];
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, options, className = '', id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div data-component-id="src/components/ui/Input.tsx:135:6" data-component-path="src/components/ui/Input.tsx" data-component-line="135" data-component-end-line="167" data-component-file="Input.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%22%7D" className="w-full">
        {label && (
          <label data-component-id="src/components/ui/Input.tsx:137:10" data-component-path="src/components/ui/Input.tsx" data-component-line="137" data-component-end-line="142" data-component-file="Input.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-foreground%20mb-1.5%22%7D"
            htmlFor={inputId}
            className="block text-sm font-medium text-foreground mb-1.5"
          >
            {label}
          </label>
        )}
        <select data-component-id="src/components/ui/Input.tsx:144:8" data-component-path="src/components/ui/Input.tsx" data-component-line="144" data-component-end-line="162" data-component-file="Input.tsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22id%22%3A%22%5Bvar%3AinputId%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
          ref={ref}
          id={inputId}
          className={`
            w-full px-4 py-2.5 rounded-lg border bg-card text-foreground
            focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-all duration-200
            ${error ? 'border-destructive focus:ring-destructive' : 'border-input'}
            ${className}
          `}
          {...props}
        >
          {options.map((option) => (
            <option data-component-id="src/components/ui/Input.tsx:158:12" data-component-path="src/components/ui/Input.tsx" data-component-line="158" data-component-end-line="160" data-component-file="Input.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%7D" key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && <p data-component-id="src/components/ui/Input.tsx:163:18" data-component-path="src/components/ui/Input.tsx" data-component-line="163" data-component-end-line="163" data-component-file="Input.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1.5%20text-sm%20text-destructive%22%7D" className="mt-1.5 text-sm text-destructive">{error}</p>}
        {hint && !error && (
          <p data-component-id="src/components/ui/Input.tsx:165:10" data-component-path="src/components/ui/Input.tsx" data-component-line="165" data-component-end-line="165" data-component-file="Input.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1.5%20text-sm%20text-muted-foreground%22%7D" className="mt-1.5 text-sm text-muted-foreground">{hint}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Input;
